/* eslint-disable no-dupe-class-members */
const BRIDGE_FLAG = '__sw$proxyBridge';
const BRIDGE_INIT_TYPE = 'bridge-init';
const BRIDGE_ACK_TYPE = 'bridge-ack';
const BRIDGE_VERSION = 1;

type BridgeInitMessage = {
  [BRIDGE_FLAG]: true;
  type: typeof BRIDGE_INIT_TYPE;
  version: typeof BRIDGE_VERSION;
};

type BridgeAckMessage = {
  type: typeof BRIDGE_ACK_TYPE;
};

export type ProxyNavigationType = 'push' | 'replace' | 'reload' | 'traverse';

type EmptyPayload = Record<string, unknown>;

type ChildMessagePayloads = {
  navigate: {
    url: string;
    state: unknown;
    navigationType: ProxyNavigationType;
    sameDoc: boolean;
    letThrough?: boolean;
  };
  loaded: { url: string };
  'traverse-request': { delta: number };
  favicon: { href: string | null };
  title: { title: string };
  'service-worker-not-supported': EmptyPayload;
  'service-worker-install-failed': EmptyPayload;
  'service-worker-installed': EmptyPayload;
  keydown: Required<
    Omit<KeyboardEventInit, 'view' | 'bubbles' | 'cancelable' | 'composed'>
  >;
};

type ParentMessagePayloads = {
  load: { url: string };
  'traverse-same-doc': { url: string; state: unknown };
  'restore-state': { state: unknown };
  'stack-length': { length: number };
  wheel: {
    clientX: number;
    clientY: number;
    screenX: number;
    screenY: number;
    deltaX: number;
    deltaY: number;
    deltaZ: number;
    deltaMode: number;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    metaKey: boolean;
  };
  reload: EmptyPayload;
  'clear-site-data': EmptyPayload;
  'pause-animations': EmptyPayload;
  'resume-animations': EmptyPayload;
};

type MessageUnion<M> = {
  [K in keyof M]: { type: K } & M[K];
}[keyof M];

export type ProxyChildToParentMessage = MessageUnion<ChildMessagePayloads>;
export type ProxyParentToChildMessage = MessageUnion<ParentMessagePayloads>;

type TypedMessage = { type: string };
type Listener<T> = (message: T) => void;
type MessageType<T extends TypedMessage> = T['type'];
type MessageByType<T extends TypedMessage, K extends MessageType<T>> = Extract<
  T,
  { type: K }
>;

export interface ProxyParentBridge {
  send(message: ProxyParentToChildMessage): void;
  waitUntilConnected(timeoutMs?: number): Promise<void>;
  subscribe(listener: Listener<ProxyChildToParentMessage>): () => void;
  subscribe<K extends MessageType<ProxyChildToParentMessage>>(
    type: K,
    listener: Listener<MessageByType<ProxyChildToParentMessage, K>>
  ): () => void;
  destroy(): void;
}

export interface ProxyChildBridge {
  send(message: ProxyChildToParentMessage): void;
  waitUntilConnected(timeoutMs?: number): Promise<void>;
  subscribe(listener: Listener<ProxyParentToChildMessage>): () => void;
  subscribe<K extends MessageType<ProxyParentToChildMessage>>(
    type: K,
    listener: Listener<MessageByType<ProxyParentToChildMessage, K>>
  ): () => void;
  destroy(): void;
}

const childMessageTypeMarkers: Record<keyof ChildMessagePayloads, true> = {
  navigate: true,
  loaded: true,
  'traverse-request': true,
  favicon: true,
  title: true,
  'service-worker-not-supported': true,
  'service-worker-install-failed': true,
  'service-worker-installed': true,
  keydown: true
};

const parentMessageTypeMarkers: Record<keyof ParentMessagePayloads, true> = {
  load: true,
  'traverse-same-doc': true,
  'restore-state': true,
  'stack-length': true,
  wheel: true,
  reload: true,
  'clear-site-data': true,
  'pause-animations': true,
  'resume-animations': true
};

const childMessageTypes: ReadonlySet<string> = new Set(
  Object.keys(childMessageTypeMarkers)
);

const parentMessageTypes: ReadonlySet<string> = new Set(
  Object.keys(parentMessageTypeMarkers)
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isBridgeInit = (value: unknown): value is BridgeInitMessage =>
  isRecord(value) &&
  value[BRIDGE_FLAG] === true &&
  value.type === BRIDGE_INIT_TYPE &&
  value.version === BRIDGE_VERSION;

const isBridgeAck = (value: unknown): value is BridgeAckMessage =>
  isRecord(value) && value.type === BRIDGE_ACK_TYPE;

const isChildMessage = (value: unknown): value is ProxyChildToParentMessage =>
  isRecord(value) &&
  typeof value.type === 'string' &&
  childMessageTypes.has(value.type);

const isParentMessage = (value: unknown): value is ProxyParentToChildMessage =>
  isRecord(value) &&
  typeof value.type === 'string' &&
  parentMessageTypes.has(value.type);

type BridgeMode =
  | { role: 'parent'; getChildWindow: () => Window | null }
  | { role: 'child'; targetWindow: Window | null };

export class ProxyBridge<
  Outgoing extends TypedMessage,
  Incoming extends TypedMessage
> {
  private readonly connectWaiters = new Set<{
    resolve: () => void;
    reject: (reason?: unknown) => void;
    timer: number | null;
  }>();

  private readonly allListeners = new Set<Listener<Incoming>>();

  private readonly typedListeners = new Map<
    MessageType<Incoming>,
    Set<Listener<Incoming>>
  >();

  private readonly pending: Outgoing[] = [];

  private activePort: MessagePort | null = null;

  private connected = false;

  private retryTimer: number | null = null;

  private destroyed = false;

  private readonly onWindowMessage = (event: MessageEvent) => {
    if (this.mode.role !== 'parent') return;
    if (event.source !== this.mode.getChildWindow()) return;

    if (isBridgeInit(event.data)) {
      const [port] = event.ports;
      if (port) {
        this.attachPort(port);
      }
      return;
    }

    if (
      isRecord(event.data) &&
      event.data[BRIDGE_FLAG] === true &&
      isChildMessage(event.data)
    ) {
      this.emit(event.data as Incoming);
    }
  };

  private constructor(private readonly mode: BridgeMode) {
    if (this.mode.role === 'parent') {
      window.addEventListener('message', this.onWindowMessage);
      return;
    }
    this.startHandshake();
  }

  public static forParent(
    getChildWindow: () => Window | null
  ): ProxyBridge<ProxyParentToChildMessage, ProxyChildToParentMessage> &
    ProxyParentBridge {
    return new ProxyBridge<
      ProxyParentToChildMessage,
      ProxyChildToParentMessage
    >({
      role: 'parent',
      getChildWindow
    }) as ProxyBridge<ProxyParentToChildMessage, ProxyChildToParentMessage> &
      ProxyParentBridge;
  }

  public static forChild(
    targetWindow: Window | null
  ): ProxyBridge<ProxyChildToParentMessage, ProxyParentToChildMessage> &
    ProxyChildBridge {
    return new ProxyBridge<
      ProxyChildToParentMessage,
      ProxyParentToChildMessage
    >({
      role: 'child',
      targetWindow
    }) as ProxyBridge<ProxyChildToParentMessage, ProxyParentToChildMessage> &
      ProxyChildBridge;
  }

  public send(message: Outgoing): void {
    if (this.activePort && (this.mode.role === 'parent' || this.connected)) {
      this.activePort.postMessage(message);
      return;
    }
    this.pending.push(message);
  }

  public waitUntilConnected(timeoutMs = 5000): Promise<void> {
    if (this.connected) {
      return Promise.resolve();
    }
    if (this.destroyed) {
      return Promise.reject(
        new Error('Proxy bridge was destroyed before connecting.')
      );
    }

    return new Promise<void>((resolve, reject) => {
      const waiter = {
        resolve,
        reject,
        timer: null as number | null
      };

      if (Number.isFinite(timeoutMs) && timeoutMs >= 0) {
        waiter.timer = window.setTimeout(() => {
          this.connectWaiters.delete(waiter);
          reject(
            new Error(`Proxy bridge handshake timed out after ${timeoutMs}ms.`)
          );
        }, timeoutMs);
      }

      this.connectWaiters.add(waiter);
    });
  }

  public subscribe(listener: Listener<Incoming>): () => void;

  public subscribe<K extends MessageType<Incoming>>(
    type: K,
    listener: Listener<MessageByType<Incoming, K>>
  ): () => void;

  public subscribe(
    typeOrListener: MessageType<Incoming> | Listener<Incoming>,
    maybeListener?:
      | Listener<Incoming>
      | Listener<MessageByType<Incoming, MessageType<Incoming>>>
  ): () => void {
    if (typeof typeOrListener === 'function') {
      const listener = typeOrListener;
      this.allListeners.add(listener);
      return () => {
        this.allListeners.delete(listener);
      };
    }

    const type = typeOrListener;
    const listener = maybeListener as Listener<Incoming> | undefined;
    if (!listener) {
      return () => undefined;
    }

    const typed =
      this.typedListeners.get(type) ?? new Set<Listener<Incoming>>();
    typed.add(listener);
    this.typedListeners.set(type, typed);

    return () => {
      const current = this.typedListeners.get(type);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) {
        this.typedListeners.delete(type);
      }
    };
  }

  public destroy(): void {
    this.destroyed = true;
    if (this.mode.role === 'parent') {
      window.removeEventListener('message', this.onWindowMessage);
    }
    this.clearRetry();
    this.rejectConnectWaiters(
      new Error('Proxy bridge was destroyed before connecting.')
    );
    this.allListeners.clear();
    this.typedListeners.clear();
    this.pending.length = 0;
    if (this.activePort) {
      this.activePort.onmessage = null;
      this.activePort.close();
      this.activePort = null;
    }
  }

  private emit(message: Incoming) {
    this.allListeners.forEach((listener) => listener(message));
    const typed = this.typedListeners.get(
      message.type as MessageType<Incoming>
    );
    typed?.forEach((listener) => listener(message));
  }

  private clearRetry() {
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private resolveConnectWaiters() {
    this.connectWaiters.forEach((waiter) => {
      if (waiter.timer !== null) {
        window.clearTimeout(waiter.timer);
      }
      waiter.resolve();
    });
    this.connectWaiters.clear();
  }

  private rejectConnectWaiters(reason: Error) {
    this.connectWaiters.forEach((waiter) => {
      if (waiter.timer !== null) {
        window.clearTimeout(waiter.timer);
      }
      waiter.reject(reason);
    });
    this.connectWaiters.clear();
  }

  private flushPending() {
    if (!this.activePort) return;
    if (this.mode.role === 'child' && !this.connected) return;
    while (this.pending.length > 0) {
      const message = this.pending.shift();
      if (message) this.activePort.postMessage(message);
    }
  }

  private attachPort(port: MessagePort) {
    if (this.activePort && this.activePort !== port) {
      this.activePort.onmessage = null;
      this.activePort.close();
    }

    this.connected = this.mode.role === 'parent';
    this.activePort = port;
    this.activePort.onmessage = (event: MessageEvent) => {
      if (this.mode.role === 'child' && isBridgeAck(event.data)) {
        this.connected = true;
        this.resolveConnectWaiters();
        this.clearRetry();
        this.flushPending();
        return;
      }

      const incoming =
        this.mode.role === 'parent'
          ? isChildMessage(event.data)
            ? (event.data as Incoming)
            : null
          : isParentMessage(event.data)
          ? (event.data as Incoming)
          : null;

      if (incoming) {
        this.emit(incoming);
      }
    };
    this.activePort.start();

    if (this.mode.role === 'parent') {
      this.resolveConnectWaiters();
      this.activePort.postMessage({
        type: BRIDGE_ACK_TYPE
      } as BridgeAckMessage);
      this.flushPending();
    }
  }

  private startHandshake() {
    if (this.mode.role !== 'child') return;
    if (this.destroyed || this.connected || !this.mode.targetWindow) return;

    const channel = new MessageChannel();
    this.attachPort(channel.port1);

    try {
      this.mode.targetWindow.postMessage(
        {
          [BRIDGE_FLAG]: true,
          type: BRIDGE_INIT_TYPE,
          version: BRIDGE_VERSION
        } as BridgeInitMessage,
        '*',
        [channel.port2]
      );
    } catch {
      channel.port1.close();
      channel.port2.close();
      return;
    }

    this.clearRetry();
    this.retryTimer = window.setTimeout(() => {
      if (!this.connected) {
        this.startHandshake();
      }
    }, 750);
  }
}
